<template>
    <div class="benchmarking-tab mt-md h-100">
        <AonSpinner
            v-if="entityStore.loadingEntity"
            class="mx-auto"
            :scale="0.5"
            color="castleMoat"
        />
        <AonCoverLoading
            :loading="moat2ProductStore.productAlignmentLoading"
            :title="`Please hang tight!`"
            :message="loadingMessage"
        >
            <template #footer>
                <progress
                    v-if="moat2ProductStore.percentComplete !== 0"
                    :value="moat2ProductStore.percentComplete"
                    max="100"
                    class="mt-md progress-bar"
                />
            </template>
        </AonCoverLoading>

        <AonContainer
            v-if="
                !moat2ProductStore.productAlignmentLoading &&
                !moat2ProductStore.productAlignmentError
            "
        >
            <AonRow class="w-100">
                <AonCol class="grid-cols-12">
                    <AonCard class="" :title="`${entityStore.entity?.name} Benchmarking`">
                        <BenchmarkingManageTechAreasToolbar
                            @tech-areas-changed="fetchCompetitors"
                        />

                        <BenchmarkingCompetitorFilters
                            class="mt-md"
                            :filtered-competitors-count="competitorTableRef?.displayedRowCount ?? 0"
                            :total-competitors-count="competitorTableData.length"
                            :selected-tab="
                                benchmarkingTabs.find((tab) => tab.id === benchmarkingTabSelected)
                                    ?.value
                            "
                            @filter-selected="handleQuickFilterSelected"
                            @watchlist-selected="handleWatchlistSelected"
                        />

                        <AonTabs
                            class="light mt-md"
                            :tabs="benchmarkingTabs"
                            :current-tab="benchmarkingTabSelected"
                            bottom-border
                            @tab-change="onTabClicked"
                        />
                        <KeepAlive>
                            <BenchmarkingInnovationRankTab
                                v-if="benchmarkingTabSelected === 2"
                                :data-loading="competitorsYearlyLoading || competitorsLoading"
                                :chart-data="competitorYearlyTableData"
                                :competitors="topCompetitors"
                                :table-data="competitorTableData"
                                :table-loading="competitorsLoading"
                                :tech-area-ids="[...moat2ProductStore.techAreaIdCollection]"
                                :quick-filter="selectedFilter"
                                @filter-change="handleCompetitorTableFilterChange"
                                ref="innovationRankTabRef"
                            />
                        </KeepAlive>

                        <KeepAlive>
                            <BenchmarkingInvestmentTab
                                v-if="benchmarkingTabSelected === 3"
                                :quick-filter="selectedFilter"
                                :selected-watchlist="selectedWatchlist?.itemValue"
                                :tech-area-ids="[...moat2ProductStore.techAreaIdCollection]"
                            />
                        </KeepAlive>

                        <KeepAlive>
                            <BenchmarkingTechAreasTab
                                v-if="benchmarkingTabSelected === 4"
                                :quick-filter="selectedFilter"
                                :selected-watchlist="selectedWatchlist?.itemValue"
                            />
                        </KeepAlive>
                    </AonCard>
                </AonCol>
            </AonRow>
        </AonContainer>
        <ErrorWithProductAlignment
            @get-product-alignment="getProductSegmentationData"
            @get-previous-run="getPreviousRunOrRequestNew(true)"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onActivated } from 'vue'
import { useEntityStore, useMoat2ProductStore } from '@/stores'
import { inject } from 'vue'

import ErrorWithProductAlignment from '@/components/helpers/ErrorWithProductAlignment.vue'
import BenchmarkingCompetitorFilters from './components/BenchmarkingCompetitorFilters.vue'
import BenchmarkingManageTechAreasToolbar from './components/BenchmarkingManageTechAreasToolbar.vue'
import BenchmarkingInnovationRankTab from './tabs/BenchmarkingInnovationRankTab.vue'
import BenchmarkingInvestmentTab from './tabs/BenchmarkingInvestmentTab.vue'
import BenchmarkingTechAreasTab from './tabs/BenchmarkingTechAreasTab.vue'
import { getCompetitors, getCompetitorsBreakdown } from '@/api/productAlignment.js'

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')

const competitorsLoading = ref(false)
const competitorsYearlyLoading = ref(false)
const competitorTableData = ref([])
const competitorYearlyTableData = ref([])
const topCompetitors = ref([])
const selectedFilter = ref('all')
const selectedWatchlist = ref(null)
const innovationRankTabRef = ref(null)

const competitorTableRef = computed(() => {
    return innovationRankTabRef.value?.competitorTableRef
})

const benchmarkingTabs = ref([
    {
        text: 'Innovation',
        value: 'momentum',
        id: 2,
    },
    {
        text: 'Investment Landscape',
        value: 'investment',
        id: 3,
    },
    {
        text: 'Innovation Details',
        value: 'tech-areas',
        id: 4,
    },
])

const benchmarkingTabSelected = ref(2)
const numTopCompetitors = ref(5)
const loadingMessage = computed(() => {
    if (moat2ProductStore.needsNewAlignment) {
        return `We are aligning patents to the product and technology areas for ${entityStore.entity?.name}. This process will take 5-10 minutes if not cached in our system. Please feel free to check out the other tabs and this process will continue in the background.`
    } else {
        return `Loading Moat2Product for ${entityStore.entity?.name}`
    }
})

const getPreviousRunOrRequestNew = async (getPreviousRun = false) => {
    await moat2ProductStore.getPreviousRunOrRequestNew(getPreviousRun)
}

const getProductSegmentationData = async (alignmentPk) => {
    await moat2ProductStore.getProductSegmentationData(alignmentPk)
}

const fetchCompetitors = async () => {
    competitorsLoading.value = true
    try {
        const response = await getCompetitors(
            moat2ProductStore.alignmentPk,
            [...moat2ProductStore.techAreaIdCollection],
            selectedWatchlist.value?.itemValue,
            true
        )
        competitorTableData.value = response.data
        topCompetitors.value = response.data.slice(0, numTopCompetitors.value)
        competitorsLoading.value = false
    } catch (error) {
        if (error.name === 'CanceledError') {
        } else {
            competitorsLoading.value = false
        }
    }
}

const fetchCompetitorsYearly = async () => {
    competitorsYearlyLoading.value = true
    try {
        const response = await getCompetitorsBreakdown(
            moat2ProductStore.alignmentPk,
            [...moat2ProductStore.techAreaIdCollection],
            topCompetitors.value.map((competitor) => competitor.aonEntityPk),
            selectedWatchlist.value?.itemValue
        )
        competitorYearlyTableData.value = response.data
        competitorsYearlyLoading.value = false
    } catch (error) {
        if (error.name === 'CanceledError') {
        } else {
            console.error('Error fetching competitors yearly:', error)
            competitorsYearlyLoading.value = false
        }
    }
}

const handleQuickFilterSelected = async (filter) => {
    const previousFilter = selectedFilter.value
    selectedFilter.value = filter
    if (previousFilter === 'watchlist' && filter !== 'watchlist') {
        selectedWatchlist.value = null
        await fetchCompetitors()
    } else if (
        previousFilter !== 'watchlist' &&
        filter === 'watchlist' &&
        selectedWatchlist.value
    ) {
        await fetchCompetitors()
    }
}

const handleCompetitorTableFilterChange = (filteredRows) => {
    topCompetitors.value = filteredRows.slice(0, numTopCompetitors.value)
}

const handleWatchlistSelected = async (watchlist) => {
    selectedWatchlist.value = watchlist
    await fetchCompetitors()
}

const onTabClicked = (tab) => {
    benchmarkingTabSelected.value = tab.id
}

// Watchers
watch(
    () => moat2ProductStore.productAlignmentTechAreaNodes,
    async (newVal, oldVal) => {
        if (newVal && newVal.size > 0 && (!oldVal || !areSetsEqual(newVal, oldVal))) {
            await fetchCompetitors()
        }
    },
    { immediate: true, deep: true }
)

watch(
    () => topCompetitors.value,
    async (newVal, oldVal) => {
        if (
            newVal &&
            newVal.length > 0 &&
            (oldVal?.length === 0 || !areSetsEqual(new Set(newVal), new Set(oldVal)))
        ) {
            await fetchCompetitorsYearly()
        }
    },
    { immediate: true, deep: true }
)

const areSetsEqual = (setA, setB) => {
    if (setA?.size !== setB?.size) return false
    return [...setA].every((item) => setB.has(item))
}
</script>

<style lang="scss" scoped>
.benchmarking-tab {
    position: relative;
    .light {
        border-color: var(--color-stone-200) !important;
    }
}
</style>
